import React, { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check, X, Sparkles, Zap, Crown, Star,
  Shield, Mic, Video, Brain, BarChart2,
  RefreshCw, Infinity, ChevronDown
} from 'lucide-react';
import { createCheckoutSession } from '../../services/stripeService';
import { PRICES } from '../../constants/subscriptionLimits';

const FREE_FEATURES = [
  { text: '10 tâches par jour', included: true },
  { text: 'Vue journalière et hebdomadaire', included: true },
  { text: 'Gamification (XP, streaks, badges)', included: true },
  { text: 'Mode focus (3 sessions/jour)', included: true },
  { text: 'Suivi d\'humeur et d\'énergie', included: true },
  { text: 'Bloc-notes texte', included: true },
  { text: '1 génération IA / semaine', included: true },
  { text: 'Notes audio et vidéo', included: false },
  { text: 'Génération IA illimitée', included: false },
  { text: 'Sync multi-appareils', included: false },
  { text: 'Statistiques avancées', included: false },
];

const PRO_FEATURES = [
  { text: 'Tâches illimitées', icon: Infinity },
  { text: 'Génération IA illimitée', icon: Brain },
  { text: 'Notes audio + transcription', icon: Mic },
  { text: 'Notes vidéo illimitées', icon: Video },
  { text: 'Complétion IA dans les notes', icon: Sparkles },
  { text: 'Google Calendar sync complet', icon: RefreshCw },
  { text: 'Statistiques historique complet', icon: BarChart2 },
  { text: 'Mode focus illimité', icon: Zap },
  { text: 'Sync multi-appareils', icon: Shield },
  { text: 'Support prioritaire', icon: Star },
];

const ANNUAL_EXTRAS = [
  'Accès aux fonctionnalités bêta',
  'Badge exclusif "Fondateur"',
  'Influence sur la roadmap',
  'Export PDF de ses statistiques',
];

const FAQ = [
  {
    q: 'Puis-je annuler à tout moment ?',
    a: 'Oui, absolument. Vous pouvez annuler votre abonnement à tout moment depuis votre espace de gestion. Votre accès Pro reste actif jusqu\'à la fin de la période payée.',
  },
  {
    q: 'Mes données sont-elles sauvegardées si je passe gratuit ?',
    a: 'Oui. Toutes vos données (tâches, historique, notes) sont conservées. Vous aurez simplement accès aux fonctionnalités gratuites.',
  },
  {
    q: 'Quels moyens de paiement sont acceptés ?',
    a: 'Carte bancaire (Visa, Mastercard, American Express), Apple Pay, Google Pay et PayPal via Stripe — la plateforme de paiement la plus sécurisée au monde.',
  },
  {
    q: 'Y a-t-il une période d\'essai gratuite ?',
    a: 'L\'application est entièrement gratuite pour commencer. Les fonctionnalités Pro se débloquent immédiatement après l\'abonnement.',
  },
  {
    q: 'L\'app fonctionne-t-elle hors-ligne ?',
    a: 'Oui ! L\'app est une PWA (Progressive Web App) — elle fonctionne hors-ligne et se synchronise quand vous retrouvez une connexion (Pro uniquement pour la sync multi-appareils).',
  },
];

const FAQItem = memo(function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border-b cursor-pointer"
      style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      onClick={() => setOpen((v) => !v)}
    >
      <div className="flex items-center justify-between py-4 px-1">
        <span className="text-sm font-medium text-zinc-200">{q}</span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
        </motion.div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <p className="text-sm text-zinc-400 pb-4 px-1 leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

export default function PricingPage({ userId, userEmail, isPro, onClose }) {
  const [billing, setBilling] = useState('annual'); // 'monthly' | 'annual'
  const [loading, setLoading] = useState(null);

  const handleSubscribe = async (type) => {
    const priceId = type === 'monthly' ? PRICES.monthly.id : PRICES.annual.id;
    setLoading(type);
    try {
      await createCheckoutSession(priceId, userId, userEmail);
    } catch (err) {
      console.error('[PricingPage] Erreur abonnement:', err);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div
      className="min-h-screen pt-[52px] pb-[100px] overflow-y-auto"
      style={{ background: '#0e0e14' }}
    >
      {/* Header */}
      <div className="text-center px-6 pt-8 pb-6">
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4"
          style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)' }}
        >
          <Crown className="w-3.5 h-3.5 text-violet-400" />
          <span className="text-xs font-semibold text-violet-300">Débloquez tout</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="text-2xl font-bold text-white mb-2"
        >
          Passez à <span style={{ color: '#a78bfa' }}>ADHD Planner Pro</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="text-sm text-zinc-400 max-w-xs mx-auto leading-relaxed"
        >
          L'app de productivité ADHD la plus complète en français — IA illimitée, notes audio/vidéo, sync cloud.
        </motion.p>

        {/* Billing toggle */}
        <div
          className="inline-flex mt-5 p-1 rounded-xl"
          style={{ background: 'rgba(255,255,255,0.05)' }}
        >
          <button
            onClick={() => setBilling('monthly')}
            className="px-4 py-2 rounded-lg text-xs font-semibold transition-all"
            style={{
              background: billing === 'monthly' ? 'rgba(139,92,246,0.25)' : 'transparent',
              color: billing === 'monthly' ? '#c4b5fd' : 'rgba(255,255,255,0.4)',
            }}
          >
            Mensuel
          </button>
          <button
            onClick={() => setBilling('annual')}
            className="px-4 py-2 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5"
            style={{
              background: billing === 'annual' ? 'rgba(139,92,246,0.25)' : 'transparent',
              color: billing === 'annual' ? '#c4b5fd' : 'rgba(255,255,255,0.4)',
            }}
          >
            Annuel
            <span
              className="text-[9px] px-1.5 py-0.5 rounded-full font-bold"
              style={{ background: '#16a34a', color: 'white' }}
            >
              -30%
            </span>
          </button>
        </div>
      </div>

      {/* Pricing cards */}
      <div className="px-4 space-y-4 max-w-md mx-auto">

        {/* FREE */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="rounded-2xl p-5"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Gratuit</div>
              <div className="text-3xl font-bold text-white">€0</div>
              <div className="text-xs text-zinc-600">Pour toujours</div>
            </div>
            {isPro === false && (
              <span className="text-[10px] font-bold px-2 py-1 rounded-lg"
                style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}>
                Plan actuel
              </span>
            )}
          </div>
          <div className="space-y-2">
            {FREE_FEATURES.map((f, i) => (
              <div key={i} className="flex items-center gap-2">
                {f.included
                  ? <Check className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
                  : <X className="w-3.5 h-3.5 text-zinc-700 flex-shrink-0" />
                }
                <span className={`text-xs ${f.included ? 'text-zinc-400' : 'text-zinc-600 line-through'}`}>
                  {f.text}
                </span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* PRO (featured) */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-2xl p-5 relative overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, rgba(124,58,237,0.15) 0%, rgba(109,40,217,0.08) 100%)',
            border: '2px solid rgba(139,92,246,0.5)',
            boxShadow: '0 8px 32px rgba(124,58,237,0.15)',
          }}
        >
          {/* Glow */}
          <div
            className="absolute -top-12 -right-12 w-32 h-32 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.2) 0%, transparent 70%)' }}
          />

          <div className="flex items-center gap-2 mb-1">
            <Crown className="w-3.5 h-3.5 text-violet-400" />
            <span className="text-xs font-bold text-violet-400 uppercase tracking-wider">Pro</span>
            <span
              className="text-[9px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: '#7c3aed', color: 'white' }}
            >
              Le plus populaire
            </span>
          </div>

          <div className="flex items-baseline gap-2 mb-1 mt-2">
            <div className="text-3xl font-bold text-white">
              {billing === 'annual'
                ? `€${PRICES.annual.monthlyEquivalent}`
                : `€${PRICES.monthly.amount}`
              }
            </div>
            <span className="text-sm text-zinc-400">/mois</span>
            {billing === 'annual' && (
              <span className="text-xs text-zinc-500">(€{PRICES.annual.amount}/an)</span>
            )}
          </div>

          {billing === 'annual' && (
            <div
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold mb-3"
              style={{ background: 'rgba(22,163,74,0.2)', color: '#4ade80' }}
            >
              <Check className="w-2.5 h-2.5" />
              Économisez €22/an
            </div>
          )}

          <div className="space-y-2.5 mb-5">
            {PRO_FEATURES.map((f, i) => (
              <div key={i} className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(124,58,237,0.3)' }}
                >
                  <f.icon className="w-2.5 h-2.5 text-violet-400" />
                </div>
                <span className="text-xs text-zinc-200">{f.text}</span>
              </div>
            ))}
          </div>

          {billing === 'annual' && (
            <div className="mb-4 pt-3 border-t" style={{ borderColor: 'rgba(139,92,246,0.2)' }}>
              <p className="text-[10px] font-semibold text-violet-400 mb-2">+ Exclusif annuel</p>
              {ANNUAL_EXTRAS.map((e, i) => (
                <div key={i} className="flex items-center gap-1.5 mb-1">
                  <Star className="w-2.5 h-2.5 text-yellow-400 flex-shrink-0" />
                  <span className="text-[10px] text-zinc-400">{e}</span>
                </div>
              ))}
            </div>
          )}

          {isPro ? (
            <div
              className="w-full py-3 rounded-xl text-center text-sm font-bold"
              style={{ background: 'rgba(22,163,74,0.2)', color: '#4ade80' }}
            >
              <Check className="w-4 h-4 inline mr-1.5" />
              Abonnement actif
            </div>
          ) : (
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => handleSubscribe(billing)}
              disabled={!!loading}
              className="w-full py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all"
              style={{
                background: loading ? 'rgba(124,58,237,0.4)' : 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                color: 'white',
                boxShadow: loading ? 'none' : '0 4px 16px rgba(124,58,237,0.4)',
              }}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Redirection...
                </>
              ) : (
                <>
                  <Crown className="w-4 h-4" />
                  {billing === 'annual' ? `Passer Pro — €${PRICES.annual.amount}/an` : `Passer Pro — €${PRICES.monthly.amount}/mois`}
                </>
              )}
            </motion.button>
          )}

          <p className="text-[10px] text-zinc-600 text-center mt-2">
            Annulation possible à tout moment · Paiement sécurisé Stripe
          </p>
        </motion.div>
      </div>

      {/* Social proof */}
      <div className="px-6 mt-6 text-center">
        <div className="flex items-center justify-center gap-1 mb-2">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
          ))}
        </div>
        <p className="text-xs text-zinc-500 max-w-xs mx-auto">
          "Enfin une app qui comprend vraiment le TDAH. La génération IA de semaine m'a changé la vie."
        </p>
        <p className="text-[10px] text-zinc-600 mt-1">— Marie L., diagnostiquée TDAH à 28 ans</p>
      </div>

      {/* FAQ */}
      <div className="px-4 mt-8 max-w-md mx-auto">
        <h2 className="text-sm font-bold text-zinc-300 mb-3 px-1">Questions fréquentes</h2>
        {FAQ.map((item, i) => (
          <FAQItem key={i} {...item} />
        ))}
      </div>

      {/* Trust badges */}
      <div className="flex items-center justify-center gap-6 mt-8 px-4 pb-4">
        <div className="flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5 text-zinc-500" />
          <span className="text-[10px] text-zinc-600">Paiement SSL Stripe</span>
        </div>
        <div className="flex items-center gap-1.5">
          <RefreshCw className="w-3.5 h-3.5 text-zinc-500" />
          <span className="text-[10px] text-zinc-600">Annulation facile</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Star className="w-3.5 h-3.5 text-zinc-500" />
          <span className="text-[10px] text-zinc-600">Satisfaction garantie</span>
        </div>
      </div>
    </div>
  );
}
